import type { ApiClient } from "@positive/api-client";
import { bilingualName, type BilingualNameLike, type DigitShape } from "@positive/format";
import { isLanguage, type Language } from "@positive/i18n";

/** `GET /api/platform/me` (identity contract §3). */
export interface SessionUser {
  id: string;
  keycloakUserId: string;
  username: string;
  email: string | null;
  displayName: string;
  language: Language | null;
  status: "active" | "disabled";
}

export interface SessionTenant {
  id: string;
  slug: string;
  nameAr: string;
  nameEn: string;
  status: "active" | "read_only" | "suspended" | "pending_deletion";
  defaultLanguage: Language;
  defaultDirection: "rtl" | "ltr";
  timeZone: string;
  modules: readonly string[];
  featureFlags: readonly string[];
  /** Digit shape of the tenant setting `platform.locale` (A5): the "organisation default". */
  defaultDigits: DigitShape;
  /** Hijri display of the tenant setting `platform.locale` (ADR-0009/D-5). */
  defaultHijriDisplay: boolean;
}

export interface SessionCompany {
  id: string;
  nameAr: string;
  nameEn: string;
  timeZone: string;
  functionalCurrencyCode: string;
}

export interface SessionBranch {
  id: string;
  companyId: string;
  nameAr: string;
  nameEn: string;
  effectiveTimeZone: string;
}

export interface SessionSelection {
  companyId: string | null;
  branchId: string | null;
  selectionRequired: boolean;
  /**
   * "All my companies" mode (ADR-0003 note 12): lists show every company of the user's data scope instead of the
   * session company only. Writes always use the session company.
   */
  allCompanies?: boolean;
}

export type ScopeValue = "all" | readonly string[];

export interface SessionScopes {
  companies: ScopeValue;
  branches: ScopeValue;
  departments: ScopeValue;
  accountGroups: ScopeValue;
  ownDocumentsOnly: boolean;
}

export interface IdlePolicy {
  backOfficeIdleMinutes: number;
  posLockMinutes: number;
  maxSessionHours: number;
}

export interface SessionLocale {
  language: Language;
  direction: "rtl" | "ltr";
  digits: DigitShape;
  /** Show Hijri dates next to Gregorian ones (typed setting `platform.locale`). */
  hijriDisplay?: boolean;
}

/**
 * Display preferences stored on the user's account (`preferences` in `/me`, identity contract §5a, decision I6);
 * `null` = default (tenant language, browser theme, tenant digit shape, tenant Hijri setting).
 */
export interface AccountPreferences {
  language: Language | null;
  theme: "morning" | "evening" | null;
  digits: DigitShape | null;
  hijriDisplay: boolean | null;
}

export interface Impersonation {
  sessionId: string;
  operator: string;
  readOnly: boolean;
  expiresAt: string;
}

export interface SessionContext {
  user: SessionUser;
  tenant: SessionTenant;
  companies: readonly SessionCompany[];
  branches: readonly SessionBranch[];
  session: SessionSelection;
  permissions: readonly string[];
  scopes: SessionScopes;
  idlePolicy: IdlePolicy;
  locale: SessionLocale;
  /** The user's stored choices; `locale` holds the effective values. */
  preferences: AccountPreferences;
  impersonation: Impersonation | null;
}

export const sessionQueryKey = ["platform", "me"] as const;

const defaultIdlePolicy: IdlePolicy = { backOfficeIdleMinutes: 30, posLockMinutes: 5, maxSessionHours: 12 };

/**
 * Normalises the `/me` answer defensively: a missing optional part falls back to the safe default (no permissions,
 * default idle policy, tenant language) instead of breaking the shell.
 */
type TenantDefaults = "modules" | "featureFlags" | "defaultDigits" | "defaultHijriDisplay";

export type RawSessionContext = Pick<SessionContext, "user"> & {
  tenant: Omit<SessionTenant, TenantDefaults> & Partial<Pick<SessionTenant, TenantDefaults>>;
  companies?: readonly SessionCompany[] | null;
  branches?: readonly SessionBranch[] | null;
  session?: SessionSelection | null;
  permissions?: readonly string[] | null;
  scopes?: SessionScopes | null;
  idlePolicy?: Partial<IdlePolicy> | null;
  locale?: Partial<SessionLocale> | null;
  preferences?: Partial<Record<keyof AccountPreferences, unknown>> | null;
  impersonation?: Impersonation | null;
};

const unrestricted: SessionScopes = {
  companies: "all",
  branches: "all",
  departments: "all",
  accountGroups: "all",
  ownDocumentsOnly: false,
};

/** Normalises the stored `preferences` of `/me`: anything unknown is `null` (= default). */
export function normalizeAccountPreferences(
  raw: Partial<Record<keyof AccountPreferences, unknown>> | null | undefined,
): AccountPreferences {
  return {
    language: isLanguage(raw?.language) ? raw.language : null,
    theme: raw?.theme === "morning" || raw?.theme === "evening" ? raw.theme : null,
    digits: raw?.digits === "latn" || raw?.digits === "arab" ? raw.digits : null,
    hijriDisplay: typeof raw?.hijriDisplay === "boolean" ? raw.hijriDisplay : null,
  };
}

export function normalizeSession(raw: RawSessionContext): SessionContext {
  const tenantLanguage = isLanguage(raw.tenant.defaultLanguage) ? raw.tenant.defaultLanguage : "ar";
  const preferences = normalizeAccountPreferences(raw.preferences);
  // The user's stored choice wins, else the server's effective language, else the tenant default (ADR-0009/D-6).
  const language =
    preferences.language ??
    (isLanguage(raw.locale?.language)
      ? raw.locale.language
      : isLanguage(raw.user.language)
        ? raw.user.language
        : tenantLanguage);
  const digits: DigitShape = raw.locale?.digits === "arab" ? "arab" : "latn";
  const localeHijri = raw.locale?.hijriDisplay === true;
  return {
    user: raw.user,
    scopes: raw.scopes ?? unrestricted,
    tenant: {
      ...raw.tenant,
      modules: raw.tenant.modules ?? [],
      featureFlags: raw.tenant.featureFlags ?? [],
      // Older answers without the tenant defaults: the effective value is the tenant's when the user chose nothing.
      defaultDigits: raw.tenant.defaultDigits ?? (preferences.digits === null ? digits : "latn"),
      defaultHijriDisplay: raw.tenant.defaultHijriDisplay ?? (preferences.hijriDisplay === null && localeHijri),
    },
    companies: raw.companies ?? [],
    branches: raw.branches ?? [],
    session: raw.session ?? { companyId: null, branchId: null, selectionRequired: false },
    permissions: raw.permissions ?? [],
    idlePolicy: { ...defaultIdlePolicy, ...raw.idlePolicy },
    locale: {
      language,
      direction: language === "ar" ? "rtl" : "ltr",
      digits,
      hijriDisplay: preferences.hijriDisplay ?? localeHijri,
    },
    preferences,
    impersonation: raw.impersonation ?? null,
  };
}

export async function fetchSession(client: ApiClient, signal?: AbortSignal): Promise<SessionContext> {
  return normalizeSession(await client.get<RawSessionContext>("platform/me", signal ? { signal } : {}));
}

/** `POST /api/platform/session/context` (contract §4): switches company/branch server-side (P12). */
export async function switchSessionContext(
  client: ApiClient,
  selection: { companyId: string; branchId: string; allCompanies?: boolean },
): Promise<SessionSelection> {
  return (await client.post<SessionSelection>("platform/session/context", selection)).data;
}

/** `/me` after a save: the stored choices and the effective locale they give (tenant defaults for `null`). */
export function withAccountChoices(
  session: SessionContext,
  choices: { language: Language | null; digits: DigitShape | null; hijri: boolean | null },
): SessionContext {
  const language = choices.language ?? session.tenant.defaultLanguage;
  return {
    ...session,
    user: { ...session.user, language: choices.language },
    preferences: {
      ...session.preferences,
      language: choices.language,
      digits: choices.digits,
      hijriDisplay: choices.hijri,
    },
    locale: {
      language,
      direction: language === "ar" ? "rtl" : "ltr",
      digits: choices.digits ?? session.tenant.defaultDigits,
      hijriDisplay: choices.hijri ?? session.tenant.defaultHijriDisplay,
    },
  };
}

/**
 * `PUT /api/platform/me/preferences` (contract §5a): stores the signed-in user's own display preferences on the
 * account; answers the stored values.
 */
export async function saveAccountPreferences(
  client: ApiClient,
  preferences: AccountPreferences,
): Promise<AccountPreferences> {
  return normalizeAccountPreferences(
    (await client.put<Partial<Record<keyof AccountPreferences, unknown>>>("platform/me/preferences", preferences)).data,
  );
}

/** Bilingual name in the UI language, falling back to the other language (ADR-0009/D-4, I4). */
export function localName(entity: BilingualNameLike, language: string): string {
  return bilingualName(entity, language);
}
